import { React, useState, useEffect, useContext } from "react";
import axios from "axios";
import Moment from "moment";
import { AppContext } from '../../../../AppStore';
import {
    ResponsiveContainer, ComposedChart, Line, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';

/**
 * 태블릿 재고 그래프 모달 (조회 전용)
 *  - 좌우 < > 로 그래프 3개를 넘겨본다. 순서:
 *      0) 재고통계 - 일별 품목 재고   (/api/p/StockStatistics/DateDayGraph)  막대(재고)+선(안전재고)
 *      1) 재고조회 - 최근재고현황     (/api/p/StockInfo/RecentList)           선(재고/가용재고)
 *      2) 재고조회 - 월별재고현황     (/api/p/StockInfo/RecentMonthList)      선(재고/가용재고)
 *  props: { onClose, item_id, item_name, item_spec }
 */
const TAB_StockInfoModal = ({ onClose, item_id, item_name, item_spec }) => {
    const appStore = useContext(AppContext);

    const [idx, setIdx] = useState(0);          // 0~2
    const [dayData, setDayData] = useState([]);     // 일별(재고통계)
    const [recentData, setRecentData] = useState([]);   // 최근재고현황
    const [monthData, setMonthData] = useState([]);     // 월별재고현황
    const [loaded, setLoaded] = useState(false);

    const toNum = (v) => Number(String(v ?? '0').replaceAll(",", "")) || 0;

    // ESC 키로 모달 닫기
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        if (!item_id) return;
        setLoaded(false);

        const start_date = Moment().format("YYYY-MM-01");
        const end_date = Moment().format("YYYY-MM-DD");

        // 0) 재고통계 - 일별 품목 재고 (기간 내 날짜 채우고 직전 재고 이어감)
        const p0 = axios.get('/api/p/StockStatistics/DateDayGraph', {
            params: { start_date, end_date, status: '1', item_id },
            withCredentials: true
        }).then((res) => {
            const data = res.data ?? [];
            let arr = [];
            if (data.length > 0) {
                const safe = toNum(data[0].item_total_safe_stock_count);
                let base = data.map((it) => ({
                    stock_date: it.stock_date,
                    recent_stock: toNum(it.recent_stock) < 0 ? 0 : toNum(it.recent_stock)
                }));
                let carry = base.length > 0 ? base[0].recent_stock : 0;
                let cur = new Date(start_date);
                const last = new Date(end_date);
                while (cur <= last) {
                    const d = cur.toISOString().substr(0, 10);
                    const found = base.filter((e) => e.stock_date === d);
                    if (found.length === 0) base.push({ stock_date: d, recent_stock: carry });
                    else carry = found[0].recent_stock;
                    cur.setDate(cur.getDate() + 1);
                }
                base.sort((a, b) => (a.stock_date > b.stock_date ? 1 : a.stock_date < b.stock_date ? -1 : 0));
                arr = base.map((a) => ({ name: a.stock_date, '재고': toNum(a.recent_stock), '안전재고': safe }));
            }
            setDayData(arr);
        }).catch(() => { setDayData([]); });

        // 1) 최근재고현황
        const p1 = axios.get('/api/p/StockInfo/RecentList', {
            params: { item_id }, withCredentials: true
        }).then((res) => {
            const arr = (res.data ?? []).map((it) => ({
                name: it.date, '재고': toNum(it.recent_stock), '가용재고': toNum(it.recent_use_stock)
            }));
            setRecentData(arr);
        }).catch(() => { setRecentData([]); });

        // 2) 월별재고현황
        const p2 = axios.get('/api/p/StockInfo/RecentMonthList', {
            params: { item_id }, withCredentials: true
        }).then((res) => {
            const arr = (res.data ?? []).map((it) => ({
                name: it.dp_month_date, '재고': toNum(it.recent_stock), '가용재고': toNum(it.recent_use_stock)
            }));
            setMonthData(arr);
        }).catch(() => { setMonthData([]); });

        Promise.all([p0, p1, p2]).finally(() => setLoaded(true));
    }, [item_id]);

    const slides = [
        { title: '일별 재고 추이', type: 'bar', data: dayData },
        { title: '최근 재고 현황', type: 'line', data: recentData },
        { title: '월별 재고 현황', type: 'line', data: monthData },
    ];
    const cur = slides[idx];
    const prev = () => setIdx((i) => (i + slides.length - 1) % slides.length);
    const next = () => setIdx((i) => (i + 1) % slides.length);

    const yFmt = (t) => String(t).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const tipFmt = (v) => new Intl.NumberFormat('ko').format(v);

    const arrowSx = {
        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
        color: '#3D63AE', fontSize: '44px', lineHeight: 1,
        fontWeight: 'bold', cursor: 'pointer', zIndex: 2, userSelect: 'none',
        padding: '0 6px'
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                position: 'relative', width: '820px', maxWidth: '92vw', height: '560px', maxHeight: '88vh',
                background: '#fff', borderRadius: '12px', padding: '16px 46px', boxSizing: 'border-box',
                display: 'flex', flexDirection: 'column'
            }}>
                {/* 헤더 */}
                <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '26px', fontWeight: 'bold' }}>{item_name}</div>
                        <div style={{ fontSize: '17px', color: '#666' }}>{item_spec}</div>
                    </div>
                    <button onClick={onClose} style={{
                        width: '40px', height: '40px', border: 'none', background: 'transparent',
                        fontSize: '30px', lineHeight: 1, color: '#555', cursor: 'pointer', padding: 0
                    }}>×</button>
                </div>

                {/* 제목 + 인덱스 */}
                <div style={{ textAlign: 'center', margin: '10px 0' }}>
                    <span style={{ fontSize: '24px', fontWeight: 'bold' }}>{cur.title}</span>
                </div>

                {/* 좌우 화살표 */}
                <div onClick={prev} style={{ ...arrowSx, left: '8px' }}>‹</div>
                <div onClick={next} style={{ ...arrowSx, right: '8px' }}>›</div>

                {/* 그래프 영역 */}
                <div style={{ flex: 1, minHeight: 0 }}>
                    {!loaded ? (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', color: '#888' }}>불러오는 중…</div>
                    ) : (cur.data.length === 0 ? (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', color: '#888' }}>조회된 데이터가 없습니다.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={cur.data} margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
                                <CartesianGrid stroke="#f0f0f0" />
                                <XAxis dataKey="name" tick={{ fontSize: 14 }} />
                                <YAxis tickFormatter={yFmt} tick={{ fontSize: 14 }} />
                                <Tooltip formatter={tipFmt} />
                                <Legend wrapperStyle={{ fontSize: '16px' }} />
                                {cur.type === 'bar' ? (
                                    <>
                                        <Bar dataKey="재고" barSize={14} fill="#067ee1" isAnimationActive={false} />
                                        <Line dataKey="안전재고" type="monotone" stroke="#FF3636" strokeWidth={2} dot={false} isAnimationActive={false} />
                                    </>
                                ) : (
                                    <>
                                        <Line dataKey="재고" type="monotone" stroke="#067ee1" strokeWidth={3} isAnimationActive={false} />
                                        <Line dataKey="가용재고" type="monotone" stroke="#28a745" strokeWidth={3} isAnimationActive={false} />
                                    </>
                                )}
                            </ComposedChart>
                        </ResponsiveContainer>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TAB_StockInfoModal;
